import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { Box, LINE_OPTIONS, LoadError, Loading, eur, monthLabel } from './costsFormat';

/**
 * Tarifas y facturas: la única pestaña que escribe.
 *
 * Una tarifa no se edita: se da de alta otra con su fecha, y la vigente se
 * cierra ese día. Así los meses pasados se siguen calculando con su precio.
 */
const fecha = d => (d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
const lineName = v => LINE_OPTIONS.find(o => o.value === (v || ''))?.label || v;
const today = () => new Date().toISOString().slice(0, 10);

const amountLabel = r => {
  if (r.kind === 'factor') return String(r.amount).replace('.', ',');
  const money = r.currency === 'USD' ? `${String(r.amount).replace('.', ',')} $` : eur(r.amount);
  return r.unit ? `${money} / ${r.unit}` : money;
};

const emptyForm = () => ({ key: '', amount: '', currency: 'EUR', unit: '', business_line: '', country: '', valid_from: today(), source: '' });

const NewRate = ({ concepts, providers, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const concept = concepts.find(c => `${c.provider}:${c.concept}` === form.key);

  const pick = key => {
    const c = concepts.find(x => `${x.provider}:${x.concept}` === key);
    setForm(f => ({ ...f, key, currency: c?.currency || 'EUR', unit: c?.unit || '' }));
  };
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!concept) return;
    setBusy(true);
    try {
      await api.post('/costs/rates', {
        provider: concept.provider,
        concept: concept.concept,
        kind: concept.kind,
        amount: Number(String(form.amount).replace(',', '.')),
        currency: form.currency,
        unit: form.unit || null,
        business_line: form.business_line || null,
        country: concept.byCountry && form.country ? form.country : null,
        valid_from: form.valid_from,
        source: form.source || null,
      });
      toast.success('Tarifa guardada');
      setForm(emptyForm());
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const input = 'block mt-1 border border-slate-300 rounded-lg px-3 py-1.5 w-full';
  return (
    <form onSubmit={submit} className='grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-slate-600'>
      <label className='md:col-span-3'>
        Concepto
        <select value={form.key} onChange={e => pick(e.target.value)} className={input} required>
          <option value=''>Elige…</option>
          {concepts.map(c => (
            <option key={`${c.provider}:${c.concept}`} value={`${c.provider}:${c.concept}`}>
              {providers[c.provider]?.label || c.provider} · {c.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Importe
        <input value={form.amount} onChange={set('amount')} inputMode='decimal' className={input} required placeholder='0,0126' />
      </label>
      {concept?.kind !== 'factor' && (
        <label>
          Moneda
          <select value={form.currency} onChange={set('currency')} className={input}>
            <option value='EUR'>EUR</option>
            <option value='USD'>USD</option>
          </select>
        </label>
      )}
      <label>
        Desde
        <input type='date' value={form.valid_from} onChange={set('valid_from')} className={input} required />
      </label>
      <label>
        Línea
        <select value={form.business_line} onChange={set('business_line')} className={input}>
          {LINE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.value ? o.label : 'Cualquiera'}
            </option>
          ))}
        </select>
      </label>
      {concept?.byCountry && (
        <label>
          País (dos letras)
          <input value={form.country} onChange={set('country')} maxLength={2} className={input} placeholder='ES' />
        </label>
      )}
      <label className='md:col-span-2'>
        De dónde sale
        <input value={form.source} onChange={set('source')} className={input} placeholder='Factura de octubre, rate card de Meta…' />
      </label>
      <div className='md:col-span-3'>
        <button type='submit' disabled={busy || !concept || !form.amount} className='bg-indigo-600 text-white rounded-lg px-4 py-2 disabled:opacity-50'>
          {busy ? 'Guardando…' : 'Guardar tarifa'}
        </button>
        <span className='text-xs text-slate-500 ml-3'>Si ya hay una vigente del mismo concepto, se cierra en la fecha «Desde».</span>
      </div>
    </form>
  );
};

const Invoices = ({ month, providers }) => {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    api
      .get('/costs/invoices', { params: { month } })
      .then(({ data }) => {
        setRows(data.data || []);
        setDrafts({});
      })
      .catch(err => toast.error(getErrorMessage(err)));
  }, [month]);
  useEffect(load, [load]);

  const save = async provider => {
    const raw = drafts[provider];
    try {
      if (raw === '') await api.delete(`/costs/invoices/${month}/${provider}`);
      else await api.put(`/costs/invoices/${month}/${provider}`, { amount_eur: Number(String(raw).replace(',', '.')) });
      toast.success('Factura guardada');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!rows) return <Loading />;
  const byProvider = new Map(rows.map(r => [r.provider, r]));
  const list = Object.entries(providers).filter(([, p]) => p.channel !== null);

  return (
    <table className='w-full text-sm'>
      <thead className='text-xs text-slate-500 text-left'>
        <tr>
          <th className='px-3 py-2 font-medium'>Proveedor</th>
          <th className='px-3 py-2 font-medium'>Factura de {monthLabel(month)} (€, sin IVA)</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {list.map(([key, p]) => {
          const saved = byProvider.get(key);
          const value = drafts[key] ?? (saved ? String(saved.amount_eur).replace('.', ',') : '');
          const dirty = drafts[key] !== undefined;
          return (
            <tr key={key} className='border-t border-slate-100'>
              <td className='px-3 py-2'>{p.label}</td>
              <td className='px-3 py-2'>
                <input
                  aria-label={`Factura ${p.label}`}
                  value={value}
                  inputMode='decimal'
                  onChange={e => setDrafts(d => ({ ...d, [key]: e.target.value }))}
                  className='border border-slate-300 rounded-lg px-3 py-1 w-36'
                  placeholder='Sin factura'
                />
              </td>
              <td className='px-3 py-2'>
                {dirty && (
                  <button type='button' onClick={() => save(key)} className='text-indigo-600 hover:underline text-sm'>
                    Guardar
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const CostRates = ({ month }) => {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    api
      .get('/costs/rates')
      .then(({ data }) => setState({ status: 'ok', data: data.data }))
      .catch(() => setState({ status: 'error', data: null }));
  }, []);
  useEffect(load, [load]);

  const seed = async () => {
    try {
      await api.post('/costs/rates/seed');
      toast.success('Tarifas iniciales cargadas');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const rates = useMemo(() => state.data?.rates || [], [state.data]);
  const visible = useMemo(() => (showHistory ? rates : rates.filter(r => r.valid_to == null)), [rates, showHistory]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') return <LoadError what='las tarifas' />;
  const { concepts, providers } = state.data;

  return (
    <div className='space-y-4'>
      <Box
        title='Tarifas'
        right={
          rates.length > 0 && (
            <label className='text-xs text-slate-600 inline-flex items-center gap-1'>
              <input type='checkbox' checked={showHistory} onChange={e => setShowHistory(e.target.checked)} />
              Ver las cerradas
            </label>
          )
        }
      >
        {rates.length === 0 ? (
          <div className='text-sm text-slate-600'>
            No hay tarifas.{' '}
            <button type='button' onClick={seed} className='text-indigo-600 hover:underline'>
              Cargar las iniciales
            </button>{' '}
            (las medidas o facturadas a 23/10/2026, cada una con su fuente).
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='text-xs text-slate-500 text-left'>
                <tr>
                  <th className='px-3 py-2 font-medium'>Proveedor</th>
                  <th className='px-3 py-2 font-medium'>Concepto</th>
                  <th className='px-3 py-2 font-medium text-right'>Importe</th>
                  <th className='px-3 py-2 font-medium'>Línea · país</th>
                  <th className='px-3 py-2 font-medium'>Vigencia</th>
                  <th className='px-3 py-2 font-medium'>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => (
                  <tr key={r._id} className={`border-t border-slate-100 ${r.valid_to ? 'text-slate-400' : ''}`}>
                    <td className='px-3 py-2'>{providers[r.provider]?.label || r.provider}</td>
                    <td className='px-3 py-2'>{r.label}</td>
                    <td className='px-3 py-2 text-right whitespace-nowrap'>{amountLabel(r)}</td>
                    <td className='px-3 py-2 text-xs'>{[r.business_line ? lineName(r.business_line) : null, r.country].filter(Boolean).join(' · ') || '—'}</td>
                    <td className='px-3 py-2 text-xs whitespace-nowrap'>
                      {fecha(r.valid_from)} → {r.valid_to ? fecha(r.valid_to) : 'vigente'}
                    </td>
                    <td className='px-3 py-2 text-xs'>
                      {r.source || '—'}
                      {r.notes ? <div className='text-amber-700'>{r.notes}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>

      <Box title='Nueva tarifa'>
        <NewRate concepts={concepts} providers={providers} onSaved={load} />
      </Box>

      <Box title='Facturas del mes'>
        <Invoices month={month} providers={providers} />
        <p className='text-xs text-slate-500 mt-2'>Vacía el importe y guarda para quitar una factura. En la fase 2 llegarán solas desde Holded.</p>
      </Box>
    </div>
  );
};

export default CostRates;
